import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHabits } from "@/context/SupabaseHabitContext";
import { useMoodTracker, MoodType } from "@/context/MoodTrackerContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, TrendingUp, Eye, EyeOff, Crown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import MoodTrackerGrid from "./MoodTrackerGrid";
import UpgradeButton from "./pricing/UpgradeButton";

interface HabitMoodStats {
  habitId: string;
  habitName: string;
  moodImpactScore: number;
  easyDaysWhenCompleted: number;
  totalCompletedDays: number;
  easyDaysWhenNotCompleted: number;
  totalNotCompletedDays: number;
  totalMoodDays: number;
  easyDaysCompleted: number;
  totalEasyDays: number;
}

export default function MoodTrackerWithAnalysis() {
  const { habits } = useHabits();
  const { moodEntries, getMoodForDate, loading: moodLoading } = useMoodTracker();
  const { isPremium } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // State for showing/hiding the entire section - read directly from localStorage
  const [showMoodSection, setShowMoodSection] = useState<boolean>(() => {
    const saved = localStorage.getItem('showMoodTracker');
    return saved ? JSON.parse(saved) : false;
  });

  // State for showing/hiding paused habits in analysis
  const [showPausedHabits, setShowPausedHabits] = useState(false);

  // Listen for custom events for immediate synchronization
  useEffect(() => {
    const handleMoodTrackerToggle = (event: CustomEvent) => {
      setShowMoodSection(event.detail.showMoodTracker);
    };

    // Listen for our custom event
    window.addEventListener('moodTrackerToggle', handleMoodTrackerToggle as EventListener);

    return () => {
      window.removeEventListener('moodTrackerToggle', handleMoodTrackerToggle as EventListener);
    };
  }, []);

  // Filter habits based on showPausedHabits setting
  const filteredHabits = habits.filter(habit => {
    if (habit.name.trim() === '') return false;
    if (showPausedHabits) return true; // Show all habits including paused
    return habit.active !== false; // Only show active habits
  });

  // Calculate Mood Impact Score for each habit
  const calculateMoodImpactScores = (): HabitMoodStats[] => {
    return filteredHabits.map(habit => {
      if (habit.completedDates.length === 0) {
        // Habit has never been completed, return neutral score
        return {
          habitId: habit.id,
          habitName: habit.name,
          moodImpactScore: 0,
          easyDaysWhenCompleted: 0,
          totalCompletedDays: 0,
          easyDaysWhenNotCompleted: 0,
          totalNotCompletedDays: 0,
          totalMoodDays: 0,
          easyDaysCompleted: 0,
          totalEasyDays: 0
        };
      }

      // Sort completion dates chronologically
      const sortedCompletions = habit.completedDates
        .map(date => new Date(date))
        .sort((a, b) => a.getTime() - b.getTime());

      // Find active periods (separated by 7+ day gaps)
      const activePeriods: { start: Date; end: Date }[] = [];
      let currentPeriodStart = sortedCompletions[0];

      for (let i = 1; i < sortedCompletions.length; i++) {
        const prevDate = sortedCompletions[i - 1];
        const currentDate = sortedCompletions[i];

        // Calculate days between completions
        const daysDiff = Math.floor((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff >= 7) {
          // Gap of 7+ days found, end current period and start new one
          activePeriods.push({
            start: currentPeriodStart,
            end: prevDate
          });
          currentPeriodStart = currentDate;
        }
      }

      // Add the final period
      if (currentPeriodStart) {
        activePeriods.push({
          start: currentPeriodStart,
          end: sortedCompletions[sortedCompletions.length - 1]
        });
      }

      // Calculate Mood Impact Score across all active periods
      let easyDaysWhenCompleted = 0;
      let totalCompletedDays = 0;
      let easyDaysWhenNotCompleted = 0;
      let totalNotCompletedDays = 0;
      let totalMoodDaysAnalyzed = 0;

      activePeriods.forEach(period => {
        // Get all unique dates within this active period where mood was logged
        const periodDates = new Set<string>();

        // Analyze all mood entries that fall within this period
        // This ensures we analyze all days where mood was tracked, regardless of habit completion
        moodEntries.forEach(entry => {
          const dateObj = new Date(entry.date);
          if (dateObj >= period.start && dateObj <= period.end) {
            totalMoodDaysAnalyzed++;
            const habitCompleted = habit.completedDates.includes(entry.date);
            const mood = entry.mood;

            if (habitCompleted) {
              totalCompletedDays++;
              if (mood === 'Easy') {
                easyDaysWhenCompleted++;
              }
            } else {
              totalNotCompletedDays++;
              if (mood === 'Easy') {
                easyDaysWhenNotCompleted++;
              }
            }
          }
        });
      });

      // Calculate Mood Impact Score (MIS)
      // %Easy_when_completed = easyDaysWhenCompleted / totalCompletedDays
      // %Easy_when_not_completed = easyDaysWhenNotCompleted / totalNotCompletedDays
      // MIS = %Easy_when_completed - %Easy_when_not_completed
      const easyWhenCompletedPercentage = totalCompletedDays > 0 ? (easyDaysWhenCompleted / totalCompletedDays) * 100 : 0;
      const easyWhenNotCompletedPercentage = totalNotCompletedDays > 0 ? (easyDaysWhenNotCompleted / totalNotCompletedDays) * 100 : 0;
      const moodImpactScore = Math.round(easyWhenCompletedPercentage - easyWhenNotCompletedPercentage);

      return {
        habitId: habit.id,
        habitName: habit.name,
        moodImpactScore,
        easyDaysWhenCompleted,
        totalCompletedDays,
        easyDaysWhenNotCompleted,
        totalNotCompletedDays,
        totalMoodDays: totalMoodDaysAnalyzed,
        easyDaysCompleted: easyDaysWhenCompleted,
        totalEasyDays: easyDaysWhenCompleted + easyDaysWhenNotCompleted
      };
    });
  };

  const habitMoodStats = moodLoading ? [] : calculateMoodImpactScores();

  // Sort habits: complete data first (by MIS score), then incomplete data
  const sortedHabits = habitMoodStats.sort((a, b) => {
    const aHasEnoughData = a.totalMoodDays >= 7;
    const bHasEnoughData = b.totalMoodDays >= 7;

    // If one has enough data and the other doesn't, prioritize the one with data
    if (aHasEnoughData && !bHasEnoughData) return -1;
    if (!aHasEnoughData && bHasEnoughData) return 1;

    // If both have enough data or both don't, sort by MIS score (highest first)
    if (aHasEnoughData && bHasEnoughData) {
      return b.moodImpactScore - a.moodImpactScore;
    }

    // If both don't have enough data, sort by mood days (most progress first)
    return b.totalMoodDays - a.totalMoodDays;
  });

  const getScoreColor = (score: number) => {
    if (score > 0) return 'hsl(142, 71%, 45%)'; // Green for positive
    if (score < 0) return 'hsl(0, 90%, 65%)'; // Red for negative
    return 'hsl(24, 95%, 65%)'; // Orange for neutral
  };

  const MoodImpactScale = ({ score }: { score: number }) => {
    // Convert score (-100 to 100) to position (0 to 100)
    const position = ((score + 100) / 200) * 100;

    return (
      <div className="w-32 flex flex-col items-center">
        <div className="text-xs text-muted-foreground mb-1 font-medium">
          {score > 0 ? '+' : ''}{score}
        </div>
        <div className="relative w-full flex items-center justify-center">
          {/* Indicator marker - positioned above the scale */}
          <div
            className="absolute w-1 h-6 bg-primary shadow-lg rounded-sm z-20 border-2 border-primary-foreground/20 transform -translate-x-1/2 transition-all duration-300"
            style={{ left: `${position}%`, top: '-6px' }}
          />

          {/* Background gradient scale - thinner */}
          <div className="relative h-2 w-full bg-muted rounded-full overflow-hidden z-10">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-400 to-green-500 rounded-full" />
          </div>
        </div>
        <div className="flex justify-between w-full text-xs text-muted-foreground mt-1">
          <span>-100</span>
          <span>+100</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      {showMoodSection && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5" style={{ color: 'hsl(142, 71%, 45%)' }} />
                <CardTitle className="text-lg">Mood Tracker & Analysis</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPausedHabits(!showPausedHabits)}
                  className="flex items-center gap-1"
                >
                  {showPausedHabits ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showPausedHabits ? 'Remove Paused' : 'Include Paused'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowMoodSection(false);
                    // Notify HabitTracker that we're hiding via the internal hide button
                    const event = new CustomEvent('moodTrackerHide', {
                      detail: { showMoodTracker: false }
                    });
                    window.dispatchEvent(event);
                  }}
                >
                  Hide
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mood Tracker Grid */}
            <div>
              <h3 className="text-base font-semibold mb-4">Daily Mood Tracking</h3>
              <MoodTrackerGrid initialMonths={isMobile ? 4 : 8} />
            </div>

            {/* Mood Impact Score Analysis */}
            <div>
              <h3 className="text-base font-semibold mb-2">Mood Impact Score</h3>
              <p className="text-sm text-muted-foreground mb-4">
                How much each habit correlates with your "Easy" mood days. Higher scores indicate habits that align with better emotional states.
              </p>

              {moodLoading ? (
                <p className="text-sm text-muted-foreground italic">
                  Loading mood data...
                </p>
              ) : filteredHabits.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  {showPausedHabits
                    ? "Add some habits and track your mood to see their impact scores."
                    : "No active habits found. Add some habits or include paused habits to see the analysis."
                  }
                </p>
              ) : sortedHabits.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  Track your mood for a few days to see habit impact scores.
                </p>
              ) : (
                <div className="space-y-4">
                  {sortedHabits.map(habit => (
                    <div key={habit.habitId} className="p-4 bg-muted/50 rounded-lg">
                      {/* Desktop Layout: Side by side */}
                      <div className="hidden md:flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium truncate">{habit.habitName}</span>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                              <TrendingUp className="h-3 w-3" />
                              <span>{habit.totalMoodDays} mood days</span>
                            </div>
                          </div>

                          {habit.totalMoodDays === 0 ? (
                            <div className="text-xs text-muted-foreground italic">
                              Log your mood daily to see how this habit affects your emotional well-being
                            </div>
                          ) : habit.totalMoodDays < 7 ? (
                            <div className="text-xs text-muted-foreground">
                              {habit.totalMoodDays} mood days analyzed
                              <span className="ml-2 italic">
                                (need {7 - habit.totalMoodDays} more days for reliable score)
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              {habit.easyDaysWhenCompleted}/{habit.totalCompletedDays} completed days easy
                              <span className="ml-2">
                                ({Math.round((habit.easyDaysWhenCompleted / habit.totalCompletedDays) * 100)}%)
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="ml-4 flex-shrink-0">
                          {habit.totalMoodDays >= 7 ? (
                            isPremium ? (
                              <MoodImpactScale score={habit.moodImpactScore} />
                            ) : (
                              <div className="relative cursor-pointer" onClick={() => navigate('/pricing')}>
                                <div className="blur-sm select-none opacity-50">
                                  <MoodImpactScale score={habit.moodImpactScore} />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-primary/10 border border-primary/20 rounded px-2 py-1">
                                    <Crown className="h-3 w-3 inline mr-1" />
                                    <span className="text-xs font-medium">Premium</span>
                                  </div>
                                </div>
                              </div>
                            )
                          ) : habit.totalMoodDays > 0 ? (
                            isPremium ? (
                              <div className="w-32 flex flex-col items-center">
                                <div className="text-xs text-muted-foreground italic text-center">
                                  Still calculating...<br />
                                  Need {7 - habit.totalMoodDays} more days
                                </div>
                              </div>
                            ) : (
                              <div className="relative w-32 flex flex-col items-center cursor-pointer" onClick={() => navigate('/pricing')}>
                                <div className="blur-sm select-none opacity-50">
                                  <div className="text-xs text-muted-foreground italic text-center">
                                    Still calculating...<br />
                                    Need {7 - habit.totalMoodDays} more days
                                  </div>
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-primary/10 border border-primary/20 rounded px-2 py-1">
                                    <Crown className="h-3 w-3 inline mr-1" />
                                    <span className="text-xs font-medium">Premium</span>
                                  </div>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="w-32 flex flex-col items-center">
                              <div className="text-xs text-muted-foreground italic text-center">
                                Track mood to see impact
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Mobile Layout: Stacked */}
                      <div className="md:hidden">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium">{habit.habitName}</span>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <TrendingUp className="h-3 w-3" />
                            <span>{habit.totalMoodDays} mood days</span>
                          </div>
                        </div>

                        {habit.totalMoodDays === 0 ? (
                          <div className="text-xs text-muted-foreground italic mb-3">
                            Log your mood daily to see how this habit affects your emotional well-being
                          </div>
                        ) : habit.totalMoodDays < 7 ? (
                          <div className="text-xs text-muted-foreground mb-3">
                            {habit.totalMoodDays} mood days analyzed
                            <span className="ml-2 italic">
                              (need {7 - habit.totalMoodDays} more days for reliable score)
                            </span>
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground mb-3">
                            {habit.easyDaysCompleted}/{habit.totalEasyDays} Easy days
                            <span className="ml-2">
                              ({Math.round((habit.easyDaysCompleted / habit.totalEasyDays) * 100)}% completion rate)
                            </span>
                          </div>
                        )}

                        <div className="flex justify-center">
                          {habit.totalMoodDays >= 7 ? (
                            isPremium ? (
                              <MoodImpactScale score={habit.moodImpactScore} />
                            ) : (
                              <div className="relative cursor-pointer" onClick={() => navigate('/pricing')}>
                                <div className="blur-sm select-none opacity-50">
                                  <MoodImpactScale score={habit.moodImpactScore} />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-primary/10 border border-primary/20 rounded px-2 py-1">
                                    <Crown className="h-3 w-3 inline mr-1" />
                                    <span className="text-xs font-medium">Premium</span>
                                  </div>
                                </div>
                              </div>
                            )
                          ) : habit.totalMoodDays > 0 ? (
                            isPremium ? (
                              <div className="text-xs text-muted-foreground italic text-center">
                                Still calculating...<br />
                                Need {7 - habit.totalMoodDays} more days
                              </div>
                            ) : (
                              <div className="relative flex justify-center cursor-pointer" onClick={() => navigate('/pricing')}>
                                <div className="blur-sm select-none opacity-50">
                                  <div className="text-xs text-muted-foreground italic text-center">
                                    Still calculating...<br />
                                    Need {7 - habit.totalMoodDays} more days
                                  </div>
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-primary/10 border border-primary/20 rounded px-2 py-1">
                                    <Crown className="h-3 w-3 inline mr-1" />
                                    <span className="text-xs font-medium">Premium</span>
                                  </div>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="text-xs text-muted-foreground italic text-center">
                              Track mood to see impact
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isPremium && (
                <div className="mt-6 text-center p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <Crown className="h-6 w-6 text-primary mx-auto mb-2" />
                  <h4 className="text-sm font-semibold mb-1">Unlock Visual Mood Impact Scores</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    See at a glance which habits boost your mood with visual scales and correlation data.
                  </p>
                  <UpgradeButton />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 